import React, { useEffect, useRef, useState } from "react";
import { Upload, Filter, RefreshCw, FileSpreadsheet, Download } from "lucide-react";
import Datepicker from "react-tailwindcss-datepicker";
import { Pagination, Stack } from "@mui/material";
import { toast } from "react-toastify";
import stockUploadService from "../../services/stockUpload.service";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "SUCCESS", label: "Upload Success" },
  { value: "PARTIALLY_FAILED", label: "Partially Success" },
  { value: "FAILED", label: "Upload Failed" },
  { value: "PROCESSING", label: "Processing" },
];

const statusBadge = (status) => {
  const map = {
    SUCCESS: "bg-emerald-100 text-emerald-800",
    PARTIALLY_FAILED: "bg-amber-100 text-amber-800",
    FAILED: "bg-red-100 text-red-800",
    PROCESSING: "bg-blue-100 text-blue-800",
  };
  const label = {
    SUCCESS: "Upload Success",
    PARTIALLY_FAILED: "Partially Success",
    FAILED: "Upload Failed",
    PROCESSING: "Processing",
  };
  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
        map[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {label[status] || status || "—"}
    </span>
  );
};

function StockUploadList() {
  const fileInputRef = useRef(null);
  const PAGE_SIZE = 10;
  const [showFilters, setShowFilters] = useState(true);
  const [fileName, setFileName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [dateValue, setDateValue] = useState({
    startDate: null,
    endDate: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);

  const fetchList = async (page = currentPage) => {
    try {
      setIsLoading(true);
      const response = await stockUploadService.getStockUploadList({
        file_name: fileName || undefined,
        status: selectedStatus || undefined,
        start_date: dateValue.startDate || undefined,
        end_date: dateValue.endDate || undefined,
        limit: PAGE_SIZE,
        offset: page,
      });
      if (response.status) {
        setRows(response.data || []);
        setTotalCount(response.count || 0);
        setTotalPage(response.pagination?.totalPages || 0);
      } else {
        setRows([]);
        setTotalCount(0);
        setTotalPage(0);
        toast.error(response.message || "Failed to fetch uploads", {
          toastId: "stockUploadListError",
        });
      }
    } catch (error) {
      setRows([]);
      toast.error(error?.message || "Failed to fetch uploads", {
        toastId: "stockUploadListError",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFileName("");
    setSelectedStatus("");
    setDateValue({ startDate: null, endDate: null });
    setCurrentPage(1);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadSample = async () => {
    try {
      setIsDownloadingSample(true);
      const result = await stockUploadService.downloadSampleFormat();
      if (!result.status) {
        toast.error(result.message || "Failed to download sample format", {
          toastId: "stockUploadSampleError",
        });
      }
    } catch (error) {
      toast.error(error?.message || "Failed to download sample format", {
        toastId: "stockUploadSampleError",
      });
    } finally {
      setIsDownloadingSample(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      toast.error("Please upload .xlsx, .xls or .csv file", {
        toastId: "stockUploadExtError",
      });
      return;
    }

    try {
      setIsUploading(true);
      const response = await stockUploadService.uploadStockFile(file);

      if (response.status) {
        toast.success(response.message || "Stock uploaded successfully", {
          toastId: "stockUploadSuccess",
        });
        setCurrentPage(1);
        fetchList(1);
      } else {
        toast.error(response.message || "Stock upload failed", {
          toastId: "stockUploadFail",
          autoClose: 6000,
        });
        fetchList(currentPage);
      }
    } catch (error) {
      toast.error(error?.message || "Stock upload failed", {
        toastId: "stockUploadFail",
      });
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    fetchList(currentPage);
  }, [currentPage]);

  const emptyState = (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <FileSpreadsheet size={48} className="text-gray-400 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        No Stock Uploads Found
      </h3>
      <p className="text-sm text-gray-600">
        Upload an inventory Excel to add Opening Stock and feed Stock Journal.
      </p>
    </div>
  );

  return (
    <div className="p-2">
      <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Stock Upload</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
          </button>
          <button
            type="button"
            onClick={handleDownloadSample}
            disabled={isDownloadingSample}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 disabled:opacity-60"
          >
            <Download className="mr-2 h-4 w-4" />
            {isDownloadingSample ? "Downloading…" : "Sample Format"}
          </button>
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={isUploading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-60"
          >
            <Upload className="mr-2 h-4 w-4" />
            {isUploading ? "Uploading…" : "Upload Stock File"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>

      {showFilters && (
        <div className="p-3 bg-white rounded-lg shadow mb-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-[220px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                File Name
              </label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name…"
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-[40px] text-sm outline-none focus:border-emerald-500"
              />
            </div>
            <div className="w-[180px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-[40px] text-sm outline-none focus:border-emerald-500 bg-white"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-[240px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Date Range
              </label>
              <Datepicker
                displayFormat="DD/MM/YYYY"
                primaryColor="emerald"
                value={dateValue}
                onChange={(newValue) => setDateValue(newValue)}
                showShortcuts={true}
                popoverDirection="down"
                containerClassName="relative w-full"
                inputClassName="w-full border border-gray-300 rounded-md px-3 py-2 h-[40px] bg-white text-sm outline-none focus:border-emerald-500"
              />
            </div>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 h-[40px] text-sm font-medium text-amber-800 bg-amber-50 rounded-md"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => {
                setCurrentPage(1);
                fetchList(1);
              }}
              className="px-4 py-2 h-[40px] text-sm font-medium text-white bg-emerald-800 rounded-md"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={() => fetchList(currentPage)}
              className="px-3 py-2 h-[40px] text-sm font-medium text-gray-600 bg-gray-100 rounded-md"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {isLoading ? (
          <div className="py-16 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          emptyState
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-emerald-50 text-emerald-900">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Uploaded by</th>
                <th className="text-left px-4 py-3 font-semibold">Total Rows</th>
                <th className="text-left px-4 py-3 font-semibold">Uploaded Rows</th>
                <th className="text-left px-4 py-3 font-semibold">Skipped Rows</th>
                <th className="text-left px-4 py-3 font-semibold">Failed Rows</th>
                <th className="text-left px-4 py-3 font-semibold">Date Time</th>
                <th className="text-left px-4 py-3 font-semibold">Original</th>
                <th className="text-left px-4 py-3 font-semibold">Summary</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const total = Number(row.total_rows) || 0;
                const uploaded = Number(row.processed_rows) || 0;
                const failed = Number(row.failed_rows) || 0;
                const skipped = Math.max(0, total - uploaded - failed);
                const dateTime =
                  row.upload_date || row.upload_time
                    ? `${row.upload_date || "—"} ${row.upload_time || ""}`.trim()
                    : "—";
                return (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 max-w-[220px]">
                    <div className="flex items-start gap-2">
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700 text-xs">
                        📄
                      </span>
                      <span
                        className="font-medium text-gray-900 break-all whitespace-normal leading-snug"
                        title={row.original_file_name}
                      >
                        {row.original_file_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{statusBadge(row.status)}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {row.uploaded_by_email || row.uploaded_by_name || row.uploaded_by}
                  </td>
                  <td className="px-4 py-3">{total}</td>
                  <td className="px-4 py-3">{uploaded}</td>
                  <td className="px-4 py-3">{skipped}</td>
                  <td className="px-4 py-3">{failed}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{dateTime}</td>
                  <td className="px-4 py-3">
                    {row.s3_url ? (
                      <a
                        href={row.s3_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-700 hover:underline"
                      >
                        Download
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.summary_s3_url ? (
                      <a
                        href={row.summary_s3_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-700 hover:underline"
                      >
                        Summary
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalCount > 0 && (
        <div className="flex justify-center mt-3">
          <Stack spacing={2}>
            <Pagination
              count={totalPage || 1}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded"
              color="primary"
            />
          </Stack>
        </div>
      )}
    </div>
  );
}

export default StockUploadList;
